// components/lists/MedicationList.tsx
import React, { useState } from 'react';
import { Search, FileText } from 'lucide-react';
import type { Medication } from '../../types/medication';
import MedicationCard from '../cards/MedicationCard';

interface MedicationListProps {
  medications: Medication[];
  onEdit: (medication: Medication) => void;
  onDelete: (id: number) => void;
  onDuplicate: (medication: Medication) => void;
}

const MedicationList: React.FC<MedicationListProps> = ({
  medications,
  onEdit,
  onDelete,
  onDuplicate
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");

  const filteredMedications = medications.filter(
    (med) =>
      med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      med.orderNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
          <FileText className="mr-2 text-blue-600" size={20} />
          Médicaments Prescrits ({medications.length})
        </h3>

        {medications.length > 0 && (
          <div className="relative w-full sm:w-auto">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Rechercher un médicament..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
      </div>

      {filteredMedications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText size={64} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">
            {medications.length === 0
              ? "Aucun médicament ajouté"
              : "Aucun résultat trouvé"}
          </p>
          <p className="text-sm mt-2">
            {medications.length === 0
              ? "Ajoutez des médicaments pour créer l'ordonnance"
              : "Essayez un autre terme de recherche"}
          </p>
        </div>
      ) : (
        <div className="space-y-4 max-h-64 sm:max-h-80 md:max-h-96 overflow-y-auto">
          {filteredMedications.map((medication: Medication) => (
            <MedicationCard
              key={medication.id}
              medication={medication}
              onEdit={() => onEdit(medication)}
              onDelete={() => onDelete(medication.id)}
              onDuplicate={() => onDuplicate(medication)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MedicationList;